/*
  Solver-Dialog für /shiftplan (SOLVER_REWRITE.md, M7 — Redesign v3, 2026-08-06).

  Zwei Ansichten in einem Dialog, der seine Breite mitwächst:
  - Übersicht (schmal): links das Formular für eine neue Planung, rechts die
    Liste der bisherigen Planungen als Karten (Zeitraum, wer sie gestartet
    hat, Status, Löschen).
  - Detail (breit): links Kennzahlen und Aktionen, rechts der Plan als echtes
    ShiftplanMonthGrid — gerahmt, kompakt, unveränderlich. Hervorgehoben sind
    ausschliesslich die neu geplanten Schichten; alles Bestehende (inkl.
    Absenzen und Ferien) steht blass dahinter.

  Pro Standort rechnet immer nur eine Planung gleichzeitig (AE-048); das
  Backend lehnt weitere mit 409/run_in_progress ab. Während des Rechnens läuft eine Rasteranimation,
  und der Dialog darf geschlossen werden — gerechnet wird im Worker weiter.
*/

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Checkbox, MenuItem, Select, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
// Dieselben Symbole wie im Sidebar-Eintrag der jeweiligen Seite.
import EventNoteIcon from "@mui/icons-material/EventNote";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { de } from "date-fns/locale";
import AppButton from "../../../../design-system/components/AppButton";
import AppCard from "../../../../design-system/components/AppCard";
import AppCardHeader from "../../../../design-system/components/AppCardHeader";
import AppCardBody from "../../../../design-system/components/AppCardBody";
import AppChip, { AppStatusChip } from "../../../../design-system/components/AppChip";
import AppConfirmDialog from "../../../../design-system/components/AppConfirmDialog";
import AppLink from "../../../../design-system/components/AppLink";
import AppMetaChip from "../../../../design-system/components/AppMetaChip";
import AppSelect from "../../../../design-system/components/AppSelect";
import AppText from "../../../../design-system/components/AppText";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconClose,
  IconInfo,
} from "../../../../design-system/icons";
import ShiftplanDialog from "../ui/ShiftplanDialog";
import ShiftplanMonthGrid from "../grid/ShiftplanMonthGrid";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";
import { getDaysInRange, parseDateValue, toIsoDate } from "../../domain/date/month";
import {
  buildVisibleEmployees,
  groupPublicHolidayNamesByDate,
  groupShiftsByCell,
} from "../../domain/model/selectors";
import { groupUserHolidaysByCell } from "../../domain/model/userHolidays";
import { buildEmployeeWorkloadMap } from "../../domain/model/workload";
import { buildDemandByDay } from "../../domain/model/demand";
import { SHIFT_DISPLAY_MODES } from "../../domain/model/shiftDisplay";
import {
  cancelSolverRun,
  createPairRule,
  createSolverRun,
  deletePairRule,
  deleteSolverRun,
  discardSolverRun,
  getSolverDirectory,
  getSolverRun,
  getSolverRunPreview,
  getSolverScopePreview,
  listPairRules,
  transferSolverRun,
} from "../../api/solverApi";
import { SOLVER_ACTIVE_STATUSES } from "./useSolverRuns";

const DETAIL_POLL_MS = 4000;
/* Übernahme: der Plan dahinter lädt sofort neu, der Dialog fliegt derweil
   weg. Beides zusammen ergibt den Eindruck, dass die Planung hinüberwandert. */
const HANDOVER_MS = 900;
/* Nach der Übernahme bleibt die Fertig-Meldung mit der Zahl der übernommenen
   Schichten kurz stehen, bevor der Dialog wegfliegt (Wunsch 31.08.). */
const TRANSFER_DONE_MS = 1800;
// Der Dialog liegt auf z-index 3300 (styles/dialog.css) — Select-Menüs müssen
// darüber, sonst öffnen sie unsichtbar dahinter. Gleiche Lösung wie im Editor.
const SELECT_MENU_PROPS = { sx: { zIndex: 3400 } };
const MODE_OPTIONS = [
  { value: "fix", label: "Bestehende Einträge behalten", hint: "Es wird um die bereits geplanten Schichten herum geplant." },
  { value: "overwrite", label: "Bestehende Einträge ersetzen", hint: "Die bereits geplanten Schichten im Zeitraum werden bei der Übernahme ersetzt." },
];
const TIME_LIMIT_OPTIONS = [
  { value: 120, label: "2 Minuten" },
  { value: 300, label: "5 Minuten" },
  { value: 600, label: "10 Minuten" },
];
const DEFAULT_TIME_LIMIT_SECONDS = 300;
const STATUS_LABELS = {
  queued: "In Warteschlange",
  running: "Wird berechnet",
  done: "Bereit zur Prüfung",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
  // „Im Schichtplan" statt „Übernommen": die Pille beantwortet damit direkt
  // die Frage, ob der Lauf schon im Plan steht (Rückmeldung 29.08.).
  transferred: "Im Schichtplan",
  discarded: "Verworfen",
};
const PREVIEW_STATUSES = new Set(["done", "transferred", "discarded"]);

function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

function defaultPeriod(referenceDate) {
  const base = referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime()) ? referenceDate : new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

function errorText(error) {
  return error?.response?.data?.message || error?.message || "Unbekannter Fehler";
}

function formatDateShort(isoDate) {
  const match = String(isoDate || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

function formatRange(run) {
  return `${formatDateShort(run.startDate)} – ${formatDateShort(run.endDate)}`;
}

function parseIsoDateParts(isoDate) {
  const match = String(isoDate || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

const MONTH_NAMES_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

// Einzeilig statt "dd.mm.yyyy – dd.mm.yyyy" (Rückmeldung 26.08.): deckt der
// Lauf genau einen ganzen Kalendermonat ab, reicht "Monat Jahr" -- sonst
// bleibt es beim exakten Zeitraum.
function formatRunPeriod(run) {
  const start = parseIsoDateParts(run.startDate);
  const end = parseIsoDateParts(run.endDate);
  const lastDayOfEndMonth = end ? new Date(end.year, end.month, 0).getDate() : null;
  const isFullMonth =
    start && end && start.year === end.year && start.month === end.month && start.day === 1 && end.day === lastDayOfEndMonth;
  return isFullMonth ? `${MONTH_NAMES_DE[start.month - 1]} ${start.year}` : formatRange(run);
}

function formatTimestamp(raw) {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const dateLabel = new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
  const timeLabel = new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit" }).format(date);
  return `${dateLabel}, ${timeLabel}`;
}

function formatClock(totalSeconds) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/*
  Statuspille mit Zustandspunkt statt reiner Textfarbe: „Bereit zur Prüfung"
  (bernstein, wartet auf eine Entscheidung) und „Im Schichtplan" (grün gefüllt
  mit Haken) sind dadurch schon aus dem Augenwinkel auseinanderzuhalten —
  vorher waren beide dasselbe blasse Grün.
*/
function StatusChip({ status }) {
  return (
    <span className={`spv2-solver-status is-${status}`}>
      {status === "transferred" ? (
        <IconCheck className="spv2-solver-status-icon" sx={{ fontSize: 13 }} aria-hidden="true" />
      ) : (
        <span className="spv2-solver-status-dot" aria-hidden="true" />
      )}
      {statusLabel(status)}
    </span>
  );
}

/*
  Rasteranimation für „wird gerade berechnet": Zellen füllen sich wellenförmig
  — dasselbe Bild wie ein Plan, der sich Stück für Stück füllt.
*/
const PLANNING_CELLS = Array.from({ length: 24 }, (_, index) => index);

function PlanningAnimation() {
  return (
    <Box className="spv2-solver-planning-anim" aria-hidden="true">
      {PLANNING_CELLS.map((index) => (
        <span
          key={index}
          className="spv2-solver-planning-cell"
          style={{ animationDelay: `${((index % 8) * 90 + Math.floor(index / 8) * 140) % 1200}ms` }}
        />
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Übersicht: Formular + Planungs-Karten                               */
/* ------------------------------------------------------------------ */

/*
  Fakten aus den Lauf-Parametern statt einer Diagnose "warum" -- der Solver
  kann eine Fehlschlag-Ursache heute nicht auf eine Abteilung zurueckfuehren
  (Grill-Entscheid 2026-08-26, Weak Point 2). Zeigt nur, was der Lauf schon
  an Ausschluessen kannte, damit sich ein Fehlschlag zumindest einordnen laesst.
*/
function RunFailureFacts({ run }) {
  if (run.status !== "failed") return null;
  const parts = [];
  if (run.excludedUserIds?.length) parts.push(`${run.excludedUserIds.length} Mitarbeitende ausgeschlossen`);
  if (run.excludedDepartmentIds?.length) {
    parts.push(
      `${run.excludedDepartmentIds.length} Abteilung${run.excludedDepartmentIds.length === 1 ? "" : "en"} ausgeschlossen`
    );
  }
  if (run.excludedShiftIds?.length) {
    parts.push(`${run.excludedShiftIds.length} Schicht${run.excludedShiftIds.length === 1 ? "" : "en"} ausgeschlossen`);
  }
  if (!parts.length) return null;
  return <span className="spv2-solver-run-facts">{parts.join(" · ")}</span>;
}

function RunCard({ run, onSelect, onDelete }) {
  const isActive = SOLVER_ACTIVE_STATUSES.has(run.status);
  // Verworfen ist endgueltig und hat nichts zu zeigen -- fehlgeschlagen schon:
  // dort steht der Grund drin, und ohne Öffnen kam man nicht an ihn heran
  // (Rückmeldung 2026-09-01). Löschen ist davon unabhaengig: das Backend
  // erlaubt es fuer jeden nicht-aktiven Lauf (DELETABLE_STATUSES), nur die
  // Oberflaeche sperrte es aus — fehlgeschlagene Laeufe blieben so liegen.
  const canOpen = run.status !== "discarded";
  const locationLabel =
    run.locationNames.length > 1 ? `${run.locationNames.length} Standorte` : run.locationNames[0] || "";
  const startedLabel = formatTimestamp(run.startedAt || run.createdAt);
  const byLabel = run.transferredByName
    ? `Übernommen von ${run.transferredByName}`
    : run.createdByName
      ? `Gestartet von ${run.createdByName}`
      : "";

  return (
    <Box className={`spv2-solver-run-card${isActive ? " is-active" : ""}`}>
      {/*
        Kopf: Nummer + Zeitraum links, Status oben rechts -- nicht mehr
        vertikal zentriert ueber die ganze Kartenhoehe (Rueckmeldung 26.08.:
        die frueheren Aktions-Buttons ueberlappten in dieser Zeile).
      */}
      <Box className="spv2-solver-run-top">
        <span className="spv2-solver-run-badge" aria-hidden="true">
          {isActive ? <span className="spv2-solver-run-badge-pulse" /> : null}
          {run.id}
        </span>
        <span className="spv2-solver-run-range">{formatRunPeriod(run)}</span>
        <span className="spv2-solver-run-state">
          {run.status === "running" && run.progress?.elapsedSeconds != null ? (
            <span className="spv2-solver-run-elapsed">{formatClock(run.progress.elapsedSeconds)}</span>
          ) : null}
          <StatusChip status={run.status} />
        </span>
      </Box>

      <Box className="spv2-solver-run-meta-block">
        <span className="spv2-solver-run-meta">
          {/* Ob Bestehendes ersetzt/behalten bleibt, nicht mehr hier
              (Rückmeldung) -- nur noch Startzeit und Standort. */}
          {[startedLabel, locationLabel].filter(Boolean).join(" · ")}
        </span>
        {byLabel ? <span className="spv2-solver-run-person">{byLabel}</span> : null}
        <RunFailureFacts run={run} />
      </Box>

      {/* Links Öffnen, rechts Löschen (Rückmeldung 31.08.: mehr braucht die
          Karte nicht -- übernommen wird in der geöffneten Planung). Per
          nth-child, damit der Löschen-Button bei aktiven Läufen fehlen kann,
          ohne den anderen zu verschieben. */}
      <Box className="spv2-solver-run-actions">
        <AppLink size="sm" disabled={!canOpen} onClick={() => onSelect(run.id)}>
          Öffnen
        </AppLink>
        {isActive ? null : (
          <button
            type="button"
            className="spv2-solver-run-delete"
            onClick={() => onDelete(run)}
            aria-label={`Planung ${formatRange(run)} löschen`}
            title="Planung löschen"
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </button>
        )}
      </Box>
    </Box>
  );
}

function ScopeSummaryRow({ label, value, loading, emphasize }) {
  return (
    <Box className={`spv2-solver-scope-row${emphasize ? " is-emphasized" : ""}`}>
      <span className="spv2-solver-scope-label">{label}</span>
      <span className="spv2-solver-scope-value">{loading ? "…" : value ?? "–"}</span>
    </Box>
  );
}

// Standorte/Abteilungen/Schichten je Standort -> AppSelect-Optionen. Bei genau
// einem Standort im Scope bleibt die Liste flach (keine Gruppen-Ueberschrift
// fuer eine einzige Gruppe); AppSelect kennt beide Formen nativ.
function toSelectOptions(groups) {
  const label = (item) => {
    const base = item.timeLabel ? `${item.name} ${item.timeLabel}` : item.name;
    // Warum diese Person schon angehakt ist, steht an ihr dran.
    return item.manualPlanning ? `${base} · manuelle Planung` : base;
  };
  if (groups.length <= 1) {
    return (groups[0]?.items || []).map((item) => ({ value: item.id, label: label(item) }));
  }
  return groups.map((group) => ({
    key: group.id,
    label: group.name,
    options: group.items.map((item) => ({ value: item.id, label: label(item) })),
  }));
}

/*
  Kartenkopf im Muster von admin_dashboard/DashboardCards.jsx
  (PlanningStatusCard): Icon+Titel links, Kennzahl+Pfeil-Link rechts, beide in
  derselben Zeile -- .ds-card__header ist bereits justify-content:space-between,
  die zwei Kind-Elemente hier tragen das ohne eigenes Flex-Layout.
*/
function SummaryCard({ icon, title, detail, loading, onNavigate, navigateLabel }) {
  return (
    <AppCard className="spv2-solver-card">
      <AppCardHeader titleVariant="compact">
        <div className="ds-card__header-main">
          <div className="ds-card__title-row">
            <span className="ds-card__visual" aria-hidden="true">
              {icon}
            </span>
            <h3 className="ds-card__title">{title}</h3>
          </div>
        </div>
        <div className="spv2-solver-card-kpi-row">
          <span className="spv2-solver-card-kpi">{loading ? "…" : detail}</span>
          <ShiftplanTooltip title={navigateLabel}>
            <button type="button" className="spv2-solver-card-link" onClick={onNavigate} aria-label={navigateLabel}>
              <IconArrowRight sx={{ fontSize: 16 }} />
            </button>
          </ShiftplanTooltip>
        </div>
      </AppCardHeader>
    </AppCard>
  );
}

/*
  Auf-/zuklappbare Karte, initial zu -- gleiches Muster wie
  scenes/demand/DemandDepartmentCard.jsx (Kopf als eigener Button mit
  aria-expanded, Rumpf bedingt gerendert statt animiert eingeklappt).
*/
function CollapsibleCard({ title, tooltip, count, summaryText, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <AppCard className="spv2-solver-card">
      <button
        type="button"
        className="spv2-solver-collapsible-header"
        aria-expanded={expanded}
        aria-label={`${title} ${expanded ? "einklappen" : "ausklappen"}`}
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="spv2-solver-collapsible-title-row">
          <AppText as="span" variant="heading-4" tone="primary">
            {title}
          </AppText>
          {tooltip ? (
            <ShiftplanTooltip title={tooltip}>
              <IconInfo sx={{ fontSize: 14 }} />
            </ShiftplanTooltip>
          ) : null}
        </span>
        <span className="spv2-solver-collapsible-meta">
          {summaryText ? (
            // Gleiche Typografie wie die Kennzahl bei Bedarf/Verfügbarkeiten
            // (SummaryCard) -- dieselbe Klasse, nicht nur derselbe Look.
            <span className="spv2-solver-card-kpi">{summaryText}</span>
          ) : count ? (
            <AppMetaChip size="xs">{count}</AppMetaChip>
          ) : null}
          <IconChevronDown
            className={`spv2-solver-collapsible-chevron${expanded ? " is-expanded" : ""}`}
            sx={{ fontSize: 20 }}
          />
        </span>
      </button>
      {expanded ? (
        <AppCardBody className="spv2-solver-card-body spv2-solver-collapsible-body">{children}</AppCardBody>
      ) : null}
    </AppCard>
  );
}

function StartView({
  referenceDate,
  directory,
  availableLocations,
  currentLocationId,
  runs,
  isBusy,
  feedback,
  onStart,
  onSelectRun,
  onDeleteRun,
  onClose,
}) {
  const navigate = useNavigate();
  const startDateLabelId = useId();
  const endDateLabelId = useId();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [existingMode, setExistingMode] = useState("fix");
  const [timeLimit, setTimeLimit] = useState(DEFAULT_TIME_LIMIT_SECONDS);
  const [locationIds, setLocationIds] = useState([]);
  const [excludedUserIds, setExcludedUserIds] = useState(() => new Set());
  const [excludedDepartmentIds, setExcludedDepartmentIds] = useState(() => new Set());
  const [excludedShiftIds, setExcludedShiftIds] = useState(() => new Set());
  const [scopePreview, setScopePreview] = useState(null);
  const [scopePreviewLoading, setScopePreviewLoading] = useState(false);
  const [pairRules, setPairRules] = useState([]);
  const [pairRuleBusy, setPairRuleBusy] = useState(false);
  const [pairRuleError, setPairRuleError] = useState("");
  const [newPair, setNewPair] = useState({ userId: "", employeeId: "", preferenceType: "preferred" });
  useEffect(() => {
    const period = defaultPeriod(referenceDate);
    setStartDate(period.start);
    setEndDate(period.end);
    setExistingMode("fix");
    setTimeLimit(DEFAULT_TIME_LIMIT_SECONDS);
    // Zuruecksetzen heisst: wieder auf die Vorauswahl "Manuelle Planung",
    // nicht auf "niemand ausgeschlossen".
    setExcludedUserIds(new Set(manualPlanningIdsRef.current));
    appliedManualIds.current = new Set(manualPlanningIdsRef.current);
    setExcludedDepartmentIds(new Set());
    setExcludedShiftIds(new Set());
  }, [referenceDate]);

  useEffect(() => {
    if (currentLocationId != null) setLocationIds([currentLocationId]);
  }, [currentLocationId]);

  // Regeln laden sich unabhaengig vom Formular -- sie sind nicht auf den
  // gewaehlten Zeitraum/Standort dieses Laufs beschraenkt (Wunsch 2026-08-26).
  useEffect(() => {
    listPairRules()
      .then(setPairRules)
      .catch(() => setPairRules([]));
  }, []);

  // Kennzahlen fuer "Umfang dieses Laufs"/"Bedarf"/"Verfuegbarkeiten" -- reine
  // Zaehlung, keine Machbarkeits-Vorhersage (siehe getSolverScopePreview-Kommentar).
  useEffect(() => {
    if (!startDate || !endDate || !locationIds.length) {
      setScopePreview(null);
      return undefined;
    }
    let ignore = false;
    setScopePreviewLoading(true);
    getSolverScopePreview({
      startDate,
      endDate,
      locationIds,
      existingMode,
      excludedDepartmentIds: Array.from(excludedDepartmentIds),
      excludedShiftIds: Array.from(excludedShiftIds),
    })
      .then((data) => {
        if (!ignore) setScopePreview(data);
      })
      .catch(() => {
        if (!ignore) setScopePreview(null);
      })
      .finally(() => {
        if (!ignore) setScopePreviewLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [startDate, endDate, locationIds, existingMode, excludedDepartmentIds, excludedShiftIds]);

  const goTo = (path) => {
    onClose?.();
    navigate(path);
  };

  // Ausschluss-Gruppen: nur die Standorte des gewählten Scopes.
  const scopeDirectory = useMemo(
    () => directory.filter((location) => locationIds.includes(location.id)),
    [directory, locationIds]
  );
  const groupsOf = (key) =>
    scopeDirectory.map((location) => ({ id: location.id, name: location.name, items: location[key] }));

  /*
    "Manuelle Planung" (Einstellung pro MA in /team) ist eine VORAUSWAHL im
    MA-Ausschluss, kein Zwang (Wunsch 2026-08-30): diese Personen sind beim
    Öffnen angehakt, lassen sich fuer einen einzelnen Lauf aber abwaehlen —
    dann plant der Solver sie dieses eine Mal mit. Beim naechsten Lauf startet
    das Formular wieder mit der Vorauswahl.
  */
  const manualPlanningIds = useMemo(() => {
    const ids = new Set();
    scopeDirectory.forEach((location) => {
      (location.users || []).forEach((user) => {
        if (user.manualPlanning) ids.add(user.id);
      });
    });
    return ids;
  }, [scopeDirectory]);
  // Merkt sich, welche Vorauswahl schon gesetzt wurde — ein bewusstes Abwaehlen
  // darf ein spaeteres Nachladen (Standort dazu) nicht wieder ueberschreiben.
  const manualPlanningIdsRef = useRef(manualPlanningIds);
  manualPlanningIdsRef.current = manualPlanningIds;
  const appliedManualIds = useRef(new Set());

  useEffect(() => {
    const fresh = Array.from(manualPlanningIds).filter((id) => !appliedManualIds.current.has(id));
    if (!fresh.length) return;
    fresh.forEach((id) => appliedManualIds.current.add(id));
    setExcludedUserIds((current) => new Set([...current, ...fresh]));
  }, [manualPlanningIds]);

  // Mitarbeitende ueber alle sichtbaren Standorte, entdoppelt -- fuer die
  // Regel-Personenauswahl (Regeln sind nicht standortgebunden).
  const employeeOptions = useMemo(() => {
    const seen = new Map();
    (directory || []).forEach((location) => {
      (location.users || []).forEach((user) => {
        if (!seen.has(user.id)) seen.set(user.id, user.name);
      });
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [directory]);

  const handleAddPairRule = async () => {
    if (!newPair.userId || !newPair.employeeId || newPair.userId === newPair.employeeId) return;
    setPairRuleBusy(true);
    setPairRuleError("");
    try {
      const created = await createPairRule(newPair);
      setPairRules((current) => [...current.filter((item) => item.id !== created.id), created]);
      setNewPair({ userId: "", employeeId: "", preferenceType: "preferred" });
    } catch (error) {
      setPairRuleError(errorText(error));
    } finally {
      setPairRuleBusy(false);
    }
  };

  const handleRemovePairRule = async (pairId) => {
    setPairRuleBusy(true);
    setPairRuleError("");
    try {
      await deletePairRule(pairId);
      setPairRules((current) => current.filter((item) => item.id !== pairId));
    } catch (error) {
      setPairRuleError(errorText(error));
    } finally {
      setPairRuleBusy(false);
    }
  };

  const showLocationSelect = availableLocations.length > 1;
  // Gesperrt wird pro Standort (AE-048): nur eine laufende Planung, die einen
  // der gewählten Standorte enthält, verhindert den Start. Ohne Auswahl
  // vorsichtig: jede laufende Planung sperrt.
  const hasRunningPlan = runs.some(
    (run) =>
      SOLVER_ACTIVE_STATUSES.has(run.status) &&
      (!locationIds.length || (run.locationIds || []).some((id) => locationIds.includes(Number(id))))
  );
  const canStart = Boolean(startDate && endDate) && (!showLocationSelect || locationIds.length > 0) && !hasRunningPlan;
  const existingModeHint = MODE_OPTIONS.find((option) => option.value === existingMode)?.hint;
  const totalExcludedCount = excludedUserIds.size + excludedDepartmentIds.size + excludedShiftIds.size;

  return (
    <Box className="spv2-solver-start">
      <Box className="spv2-solver-start-grid">
        {/* GRUNDLAGEN ------------------------------------------------------ */}
        <Box className="spv2-solver-column spv2-solver-column--grundlagen">
          <Box className="spv2-solver-column-head">
            <Box className="spv2-solver-column-title-row">
              <span className="spv2-solver-step-badge" aria-hidden="true">
                1
              </span>
              <AppText as="h2" variant="heading-3" className="spv2-solver-column-title">
                Grundlagen
              </AppText>
            </Box>
            <AppMetaChip size="xs">Pflicht</AppMetaChip>
          </Box>

          {/* Kein umschliessender Card-Rahmen mehr (Rückmeldung): die Felder
              stehen direkt in der Spalte. */}
          <Box className="spv2-solver-form-section">
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                {/* Label über dem Feld statt MUI's eigenem, schwebendem Label
                    (Rückmeldung) -- gleiches Muster wie "Standorte" etc.
                    darunter. aria-labelledby haelt den zugaenglichen Namen
                    des Felds trotzdem bei "Von"/"Bis". */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
                  <AppText as="span" id={startDateLabelId} variant="body-2" tone="secondary">
                    Von
                    <span className="ds-field__required">*</span>
                  </AppText>
                  <DatePicker
                    format="dd.MM.yyyy"
                    value={parseDateValue(startDate)}
                    onChange={(date) => setStartDate(toIsoDate(date))}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        inputProps: { "aria-labelledby": startDateLabelId },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
                  <AppText as="span" id={endDateLabelId} variant="body-2" tone="secondary">
                    Bis
                    <span className="ds-field__required">*</span>
                  </AppText>
                  <DatePicker
                    format="dd.MM.yyyy"
                    value={parseDateValue(endDate)}
                    onChange={(date) => setEndDate(toIsoDate(date))}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        inputProps: { "aria-labelledby": endDateLabelId },
                      },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </Box>

          {showLocationSelect ? (
            <Box className="spv2-solver-form-section">
              <AppText as="span" variant="body-2" tone="secondary">
                Standorte
                <span className="ds-field__required">*</span>
              </AppText>
              <Select
                size="small"
                multiple
                value={locationIds}
                onChange={(event) => {
                  const value = event.target.value;
                  setLocationIds(Array.isArray(value) ? value.map(Number) : []);
                }}
                MenuProps={SELECT_MENU_PROPS}
                renderValue={(selected) => {
                  if (!selected.length) return "Standorte wählen…";
                  if (selected.length === 1) {
                    const location = availableLocations.find((item) => item.id === selected[0]);
                    return location?.name || "1 Standort";
                  }
                  return `${selected.length} Standorte`;
                }}
                displayEmpty
                fullWidth
              >
                {availableLocations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    <Checkbox size="small" checked={locationIds.includes(location.id)} sx={{ p: 0.5, mr: 1 }} />
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : null}

          <Box className="spv2-solver-form-section">
            <Box className="spv2-solver-field-label-row">
              <AppText as="span" variant="body-2" tone="secondary">
                Bestehende Schichten
                <span className="ds-field__required">*</span>
              </AppText>
              <ShiftplanTooltip title={existingModeHint}>
                <IconInfo sx={{ fontSize: 14 }} />
              </ShiftplanTooltip>
            </Box>
            <Select
              size="small"
              value={existingMode}
              onChange={(event) => setExistingMode(event.target.value)}
              MenuProps={SELECT_MENU_PROPS}
              fullWidth
            >
              {MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box className="spv2-solver-form-section">
            <AppText as="span" variant="body-2" tone="secondary">
              Rechenzeit
              <span className="ds-field__required">*</span>
            </AppText>
            <Select
              size="small"
              value={timeLimit}
              onChange={(event) => setTimeLimit(Number(event.target.value))}
              MenuProps={SELECT_MENU_PROPS}
              fullWidth
            >
              {TIME_LIMIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <AppCard className="spv2-solver-card">
            <AppCardHeader title="Umfang dieses Laufs" titleVariant="compact" />
            <AppCardBody className="spv2-solver-card-body">
              <ScopeSummaryRow label="Tage" value={scopePreview?.days} loading={scopePreviewLoading} />
              <ScopeSummaryRow label="Schichtplätze" value={scopePreview?.shiftSlots} loading={scopePreviewLoading} />
              <ScopeSummaryRow
                label="Bestehend, bleibt"
                value={scopePreview?.keptExisting}
                loading={scopePreviewLoading}
              />
              <ScopeSummaryRow label="Zu planen" value={scopePreview?.toPlan} loading={scopePreviewLoading} emphasize />
            </AppCardBody>
          </AppCard>
        </Box>

        <Box className="spv2-solver-column-divider spv2-solver-start-divider-1" aria-hidden="true" />

        {/* OPTIONALE PRÜFUNGEN -------------------------------------------- */}
        <Box className="spv2-solver-column spv2-solver-column--optional">
          <Box className="spv2-solver-column-head">
            <Box className="spv2-solver-column-title-row">
              <span className="spv2-solver-step-badge" aria-hidden="true">
                2
              </span>
              <AppText as="h2" variant="heading-3" className="spv2-solver-column-title">
                Optionale Prüfungen
              </AppText>
            </Box>
          </Box>

          <SummaryCard
            icon={<EventNoteIcon sx={{ fontSize: 18 }} />}
            title="Bedarf"
            detail={`${scopePreview?.shiftSlots ?? "–"} Schichtplätze · ${scopePreview?.departmentCount ?? "–"} Abteilungen`}
            loading={scopePreviewLoading}
            navigateLabel="Zur Bedarfsplanung"
            onNavigate={() => goTo("/demand")}
          />

          <SummaryCard
            icon={<EventAvailableIcon sx={{ fontSize: 18 }} />}
            title="Verfügbarkeiten"
            detail={`${scopePreview?.absenceCount ?? "–"} Absenzen · ${scopePreview?.holidayCount ?? "–"} Ferien · ${scopePreview?.unavailableCount ?? "–"} nicht verfügbar`}
            loading={scopePreviewLoading}
            navigateLabel="Zur Verfügbarkeit"
            onNavigate={() => goTo("/availability")}
          />

          <CollapsibleCard title="Ausschlüsse" count={totalExcludedCount}>
            <Box className="spv2-solver-form-section">
              <Box className="spv2-solver-field-label-row">
                <AppText as="span" variant="body-2" tone="secondary">
                  Mitarbeitende
                </AppText>
                {manualPlanningIds.size ? (
                  <ShiftplanTooltip title="Personen mit „Manuelle Planung“ (Mitarbeiterverwaltung) sind vorausgewählt. Wählst du sie ab, plant der Solver sie nur für diesen Lauf mit — beim nächsten Lauf sind sie wieder ausgeschlossen.">
                    <IconInfo sx={{ fontSize: 14 }} />
                  </ShiftplanTooltip>
                ) : null}
              </Box>
              <AppSelect
                multiple
                searchable
                showSelectAll
                size="sm"
                placeholder="Keine ausgeschlossen"
                selectAllLabel="Alle Mitarbeitenden"
                options={toSelectOptions(groupsOf("users"))}
                value={Array.from(excludedUserIds)}
                onChange={(event) => setExcludedUserIds(new Set(event.target.value))}
              />
              {manualPlanningIds.size ? (
                <AppText as="span" variant="body-2" tone="secondary" className="spv2-solver-field-hint">
                  {manualPlanningIds.size === 1
                    ? "1 Person ist wegen „Manuelle Planung“ vorausgewählt."
                    : `${manualPlanningIds.size} Personen sind wegen „Manuelle Planung“ vorausgewählt.`}{" "}
                  Abwählen gilt nur für diesen Lauf.
                </AppText>
              ) : null}
            </Box>
            <Box className="spv2-solver-form-section">
              <AppText as="span" variant="body-2" tone="secondary">
                Abteilungen
              </AppText>
              <AppSelect
                multiple
                searchable
                showSelectAll
                size="sm"
                placeholder="Keine ausgeschlossen"
                selectAllLabel="Alle Abteilungen"
                options={toSelectOptions(groupsOf("departments"))}
                value={Array.from(excludedDepartmentIds)}
                onChange={(event) => setExcludedDepartmentIds(new Set(event.target.value))}
              />
            </Box>
            <Box className="spv2-solver-form-section">
              <AppText as="span" variant="body-2" tone="secondary">
                Schichten
              </AppText>
              <AppSelect
                multiple
                searchable
                showSelectAll
                size="sm"
                placeholder="Keine ausgeschlossen"
                selectAllLabel="Alle Schichten"
                options={toSelectOptions(groupsOf("shifts"))}
                value={Array.from(excludedShiftIds)}
                onChange={(event) => setExcludedShiftIds(new Set(event.target.value))}
              />
            </Box>
          </CollapsibleCard>

          <CollapsibleCard
            title="Regeln"
            summaryText={`${pairRules.length} Regeln`}
            tooltip="Regeln gelten als Wunsch, nicht als harte Bedingung — bei knapper Besetzung kann der Solver sie überstimmen, statt eine Schicht leer zu lassen."
          >
            {pairRules.length ? (
              <Box className="spv2-solver-pair-list">
                {pairRules.map((pair) => (
                  <Box key={pair.id} className="spv2-solver-pair-row">
                    <span className="spv2-solver-pair-names">
                      {pair.userName} &amp; {pair.employeeName}
                    </span>
                    <AppStatusChip
                      status={pair.preferenceType === "avoidable" ? "error" : "success"}
                      size="sm"
                      showIcon={false}
                    >
                      {pair.preferenceType === "avoidable" ? "nicht gemeinsam" : "bevorzugt"}
                    </AppStatusChip>
                    <button
                      type="button"
                      className="spv2-solver-pair-remove"
                      aria-label={`Regel ${pair.userName} & ${pair.employeeName} entfernen`}
                      disabled={pairRuleBusy}
                      onClick={() => handleRemovePairRule(pair.id)}
                    >
                      <IconClose sx={{ fontSize: 14 }} />
                    </button>
                  </Box>
                ))}
              </Box>
            ) : (
              <AppText as="span" variant="body-2" tone="secondary">
                Noch keine Regeln.
              </AppText>
            )}

            <Box className="spv2-solver-pair-add">
              <Select
                size="small"
                displayEmpty
                value={newPair.userId}
                onChange={(event) => setNewPair((current) => ({ ...current, userId: Number(event.target.value) }))}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value="">Person A</MenuItem>
                {employeeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                displayEmpty
                value={newPair.employeeId}
                onChange={(event) =>
                  setNewPair((current) => ({ ...current, employeeId: Number(event.target.value) }))
                }
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value="">Person B</MenuItem>
                {employeeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={newPair.preferenceType}
                onChange={(event) =>
                  setNewPair((current) => ({ ...current, preferenceType: event.target.value }))
                }
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value="preferred">bevorzugt</MenuItem>
                <MenuItem value="avoidable">nicht gemeinsam</MenuItem>
              </Select>
              <AppButton
                variant="outlined"
                disabled={pairRuleBusy || !newPair.userId || !newPair.employeeId}
                onClick={handleAddPairRule}
              >
                + Regel hinzufügen
              </AppButton>
            </Box>
            {pairRuleError ? (
              <Typography variant="caption" color="error">
                {pairRuleError}
              </Typography>
            ) : null}
          </CollapsibleCard>
        </Box>

        <Box className="spv2-solver-column-divider spv2-solver-start-divider-2" aria-hidden="true" />

        {/* VERLAUF -- kein Schritt, kein Badge, volle Hoehe (Rückmeldung) --- */}
        <Box className="spv2-solver-runs">
          <AppText as="h2" variant="heading-3" className="spv2-solver-column-title">
            Verlauf · {runs.length} Läufe
          </AppText>
          {runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Noch keine Planungen — links die erste starten.
            </Typography>
          ) : (
            <Box className="spv2-solver-run-list">
              {runs.map((run) => (
                <RunCard
                  key={run.id}
                  run={run}
                  onSelect={onSelectRun}
                  onDelete={onDeleteRun}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Nur unter den Schritten 1+2, nicht unter Verlauf (Rückmeldung). */}
        <Box className="spv2-solver-start-actions">
          <AppButton
            variant="primary"
            disabled={isBusy || !canStart}
            onClick={() =>
              onStart({
                startDate,
                endDate,
                existingMode,
                locationIds,
                excludedUserIds: Array.from(excludedUserIds),
                excludedDepartmentIds: Array.from(excludedDepartmentIds),
                excludedShiftIds: Array.from(excludedShiftIds),
                timeLimitSeconds: timeLimit,
              })
            }
          >
            Planung starten
          </AppButton>

          {hasRunningPlan ? (
            <Typography className="spv2-solver-hint">
              Für diesen Standort wird bereits eine Planung berechnet. Sobald sie fertig oder abgebrochen ist,
              lässt sich die nächste starten.
            </Typography>
          ) : null}

          {feedback ? (
            <Typography variant="body2" color={feedback.tone === "error" ? "error" : "success.main"}>
              {feedback.text}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Detail: Kennzahlen links, Plan rechts                               */
/* ------------------------------------------------------------------ */

/*
  Zustandsfläche für alles, was (noch) keinen Plan zu zeigen hat: rechnet,
  wartet, fehlgeschlagen, abgebrochen. Sitzt mittig in der freien Fläche —
  sonst klebt der Inhalt oben und darunter steht eine leere Seite.

  Bei einem Fehlschlag steht hier NUR die Klartext-Meldung: die frueher
  ergaenzte Faktentabelle (Zeitraum, Modus, Ausschluesse, Rechenzeit,
  technischer Status) war fuer die Zielgruppe zu technisch und ist auf
  Wunsch wieder raus (Rückmeldung 2026-09-01).
*/
function PendingState({
  status,
  progress,
  nowTick,
  isSuperAdmin,
  workerAlive = null,
  isBusy,
  cancelRequested,
  errorMessage,
  failureHints = [],
  feedback,
  onCancel,
}) {
  const isRunning = status === "running";
  const canCancel = SOLVER_ACTIVE_STATUSES.has(status);
  const limit = progress?.timeLimitSeconds || null;
  const baseElapsed = progress?.elapsedSeconds ?? null;
  // Zwischen zwei Abfragen läuft die Anzeige lokal weiter (1-s-Ticker).
  const elapsed =
    baseElapsed == null ? null : baseElapsed + Math.max(0, (nowTick - (progress?.receivedAt || nowTick)) / 1000);
  const percent = limit && elapsed != null ? Math.min(100, (elapsed / limit) * 100) : null;

  const title = {
    running: "Die Planung wird berechnet",
    // Ein Solve gleichzeitig fuer alle Firmen (AE-037): "in der Warteschlange"
    // klang nach "gleich geht's los" — das hier sagt, worauf gewartet wird.
    queued: "Wartet, bis die laufende Berechnung abgeschlossen ist",
    failed: "Die Planung konnte nicht abgeschlossen werden",
    cancelled: "Die Berechnung wurde abgebrochen",
    discarded: "Diese Planung wurde verworfen",
    // done/transferred landen hier nur, solange die Vorschau noch lädt.
    done: "Der Plan wird geladen…",
    transferred: "Der Plan wird geladen…",
  }[status];

  return (
    <Box className="spv2-solver-state">
      {isRunning ? <PlanningAnimation /> : null}
      <Typography className="spv2-solver-state-title">{title || statusLabel(status)}</Typography>

      {isRunning ? (
        <Box className="spv2-solver-progress">
          <Box className="spv2-solver-progress-track">
            <Box
              className={`spv2-solver-progress-fill${percent == null ? " is-indeterminate" : ""}`}
              style={percent == null ? undefined : { width: `${percent}%` }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {elapsed != null ? `Läuft seit ${formatClock(elapsed)}` : "Läuft…"}
            {limit ? ` von maximal ${formatClock(limit)}` : ""}
          </Typography>
        </Box>
      ) : null}

      {status === "failed" ? (
        <Box className="spv2-solver-error">
          <Typography variant="body2">
            {errorMessage || "Die Berechnung wurde ohne Fehlermeldung beendet."}
          </Typography>
          {/*
            Ursachen im Klartext (solver/diagnosis.py). Steht bewusst direkt
            unter der Meldung und ohne Aufklappen: wer hier landet, will genau
            das wissen (Rückmeldung 2026-09-01).
          */}
          {failureHints.length ? (
            <Box component="ul" className="spv2-solver-error-hints">
              {failureHints.map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {canCancel ? (
        <Typography className="spv2-solver-state-hint">
          Du kannst dieses Fenster schliessen — die Berechnung läuft im Hintergrund weiter. Sobald sie
          fertig ist, erscheint ein Hinweis beim Zauberstab in der Werkzeugleiste.
        </Typography>
      ) : null}

      {canCancel ? (
        <AppButton variant="outlined" disabled={isBusy || cancelRequested} onClick={onCancel}>
          {cancelRequested ? "Wird abgebrochen…" : "Berechnung abbrechen"}
        </AppButton>
      ) : null}

      {feedback ? (
        <Typography variant="body2" color={feedback.tone === "error" ? "error" : "success.main"}>
          {feedback.text}
        </Typography>
      ) : null}

      {/*
        Nur wenn das Backend ausdruecklich "kein Rechenknecht" meldet (false),
        nicht bei null: dann gibt es schlicht keine Aussage. Ein stiller
        Poller-Tod ist der schlimmste Fall des eingebetteten Betriebs (AE-037) —
        Laeufe warten sonst fuer immer, ohne dass jemand etwas merkt.
      */}
      {isSuperAdmin && status === "queued" && workerAlive === false ? (
        <Typography variant="caption" color="error">
          Zurzeit rechnet kein Prozess: die Planung wartet, bis das Backend wieder einen Rechenknecht
          gestartet hat. Bleibt das so, den Solver-Dienst neu starten.
        </Typography>
      ) : null}
    </Box>
  );
}

function StatCard({ value, label, tone = "neutral", barPercent = null }) {
  return (
    <Box className={`spv2-solver-stat is-${tone}`}>
      <span className="spv2-solver-stat-value">{value}</span>
      <span className="spv2-solver-stat-label">{label}</span>
      {barPercent != null ? (
        <span className="spv2-solver-stat-bar">
          <span className="spv2-solver-stat-bar-fill" style={{ width: `${Math.min(100, barPercent)}%` }} />
        </span>
      ) : null}
    </Box>
  );
}

function SuperAdminDiagnostics({ detailRun, logRef }) {
  const diagnostics = detailRun?.diagnostics || null;
  const diagStats = diagnostics?.stats || null;
  const logTail = diagnostics?.log_tail || [];
  const incumbents = diagnostics?.incumbents || [];
  const lastIncumbents = incumbents.slice(-5).reverse();

  const gapPercent = useMemo(() => {
    if (!diagStats || detailRun?.objectiveValue == null || detailRun.objectiveValue <= 0) return null;
    const bound = diagStats.objective_bound;
    if (bound == null) return null;
    return (((detailRun.objectiveValue - bound) / detailRun.objectiveValue) * 100).toFixed(1);
  }, [diagStats, detailRun?.objectiveValue]);

  if (!diagnostics && detailRun?.objectivePoints == null && !detailRun?.constraintCosts?.length) return null;

  return (
    <Box className="spv2-solver-diagnostics">
      <Typography className="spv2-solver-section-title">Diagnose (nur Super-Admin)</Typography>

      {detailRun?.objectivePoints != null ? (
        <Typography variant="body2">
          Strafpunkte: <strong>{detailRun.objectivePoints}</strong>
          {detailRun.solverStatus ? ` · ${detailRun.solverStatus}` : ""}
          {gapPercent != null ? ` · Gap ${gapPercent}%` : ""}
        </Typography>
      ) : null}

      {diagStats ? (
        <Typography variant="body2" color="text.secondary">
          {diagStats.num_variables} Variablen ({diagStats.num_candidates_raw} Kandidaten roh) ·{" "}
          {diagStats.num_employees} MA · {diagStats.num_days} Tage
          {diagStats.wall_time_seconds != null ? ` · Solve ${diagStats.wall_time_seconds}s` : ""}
        </Typography>
      ) : null}

      {lastIncumbents.length ? (
        <Box className="spv2-solver-incumbents">
          {lastIncumbents.map((item, index) => (
            <Typography key={`${item.elapsed_seconds}-${index}`} variant="caption" display="block" color="text.secondary">
              {formatClock(item.elapsed_seconds)} · beste Lösung {Math.round(item.objective / 60)} Pkt
              {item.bound != null ? ` (Schranke ${Math.round(item.bound / 60)})` : ""}
            </Typography>
          ))}
        </Box>
      ) : null}

      {detailRun?.constraintCosts?.length ? (
        <table className="spv2-solver-costs">
          <thead>
            <tr>
              <th>NB</th>
              <th>Verletzungen</th>
              <th>Strafpunkte</th>
            </tr>
          </thead>
          <tbody>
            {detailRun.constraintCosts
              .filter((cost) => cost.costTotal > 0)
              .map((cost) => (
                <tr key={cost.nbNumber}>
                  <td>{cost.nbNumber}</td>
                  <td>{cost.violationCount}</td>
                  <td>{cost.costPoints}</td>
                </tr>
              ))}
            {detailRun.constraintCosts.every((cost) => cost.costTotal === 0) ? (
              <tr>
                <td colSpan={3}>Keine Verletzungen — alle aktiven Regeln eingehalten.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : null}

      {logTail.length ? (
        <Box component="pre" ref={logRef} className="spv2-solver-log">
          {logTail.join("\n")}
        </Box>
      ) : null}
    </Box>
  );
}

function DetailView({
  run,
  detail,
  preview,
  isSuperAdmin,
  workerAlive = null,
  isBusy,
  transferPending,
  transferDone,
  feedback,
  transferOutcome,
  shiftDisplayMode,
  onBack,
  onRequestTransfer,
  onRequestDiscard,
  onCancel,
}) {
  const detailRun = detail && detail.id === run.id ? detail : run;
  const status = detailRun.status;
  const logRef = useRef(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [demandVisible, setDemandVisible] = useState(true);
  const [demandExpanded, setDemandExpanded] = useState(false);

  useEffect(() => {
    if (status !== "running") return undefined;
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  const logLength = detailRun?.diagnostics?.log_tail?.length || 0;
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLength]);

  /* ---- Plan-Vorschau aus denselben Domain-Bausteinen wie /shiftplan ---- */
  const days = useMemo(() => (preview ? getDaysInRange(preview.startDate, preview.endDate) : []), [preview]);
  const rows = useMemo(
    () => (preview ? buildVisibleEmployees({ employees: preview.employees, shifts: preview.entries, query: "" }) : []),
    [preview]
  );
  const shiftsByCell = useMemo(() => (preview ? groupShiftsByCell(preview.entries, "employee") : {}), [preview]);
  const userHolidaysByCell = useMemo(
    () => (preview ? groupUserHolidaysByCell({ holidays: preview.userHolidays, days }) : {}),
    [preview, days]
  );
  const publicHolidayNamesByDate = useMemo(
    () => (preview ? groupPublicHolidayNamesByDate(preview.publicHolidays) : {}),
    [preview]
  );
  const vacationAbsenceIds = useMemo(
    () =>
      new Set((preview?.absenceOptions || []).filter((option) => option.isVacation).map((option) => String(option.id))),
    [preview]
  );
  const workloadByEmployeeId = useMemo(
    () =>
      preview
        ? buildEmployeeWorkloadMap({
            employees: rows,
            shifts: preview.entries,
            days,
            workingDaysPerWeek: preview.workingDays || 5,
            absenceOptions: preview.absenceOptions,
            vacationAbsenceIds,
            userHolidays: preview.userHolidays,
          })
        : {},
    [preview, rows, days, vacationAbsenceIds]
  );
  const demandModel = useMemo(
    () =>
      preview
        ? buildDemandByDay({ demandEntries: preview.demandEntries, shifts: preview.entries, days })
        : { byDay: {}, hasDemand: false, totalRequired: 0, totalMissing: 0 },
    [preview, days]
  );
  /*
    Hervorgehoben werden ausschliesslich die neu geplanten Schichten. Alles
    andere — bestehende Einträge, Absenzen, Ferien — bleibt blass. Das Set
    wird auch dann übergeben, wenn es leer ist (Planung ohne neue Schichten);
    `dimUnhighlighted` sorgt dafür, dass dann trotzdem alles blass ist.
  */
  const newEntryIds = useMemo(
    () =>
      new Set(
        (preview?.entries || [])
          .filter((entry) => entry.solverSource === "solver")
          .map((entry) => String(entry.id))
      ),
    [preview]
  );

  const newCount = newEntryIds.size;
  const keptCount = useMemo(
    () => (preview?.entries || []).filter((entry) => entry.solverSource === "fixed").length,
    [preview]
  );
  const plannedEmployeeCount = useMemo(() => {
    const ids = new Set(
      (preview?.entries || [])
        .filter((entry) => entry.entryType === "shift" && entry.solverSource === "solver")
        .map((entry) => entry.employeeId)
    );
    return ids.size;
  }, [preview]);
  const coverage = useMemo(() => {
    if (!demandModel.totalRequired) return null;
    const covered = demandModel.totalRequired - demandModel.totalMissing;
    return {
      percent: Math.round((covered / demandModel.totalRequired) * 100),
      missing: demandModel.totalMissing,
    };
  }, [demandModel]);

  const showPreviewGrid = PREVIEW_STATUSES.has(status) && preview && rows.length > 0 && days.length > 0;
  const progress = detailRun.progress ? { ...detailRun.progress, receivedAt: detailRun.progressReceivedAt } : null;

  return (
    <Box className="spv2-solver-detail">
      <Box className="spv2-solver-detail-head">
        <button type="button" className="spv2-solver-back" onClick={onBack} aria-label="Zurück zur Übersicht">
          <IconArrowLeft fontSize="small" />
        </button>
        <Box className="spv2-solver-detail-title">
          <Typography className="spv2-solver-detail-name">
            Planung #{run.id} · {formatRange(run)}
          </Typography>
          <Typography className="spv2-solver-detail-meta">
            {[
              run.locationNames.length > 1 ? `${run.locationNames.length} Standorte` : run.locationNames[0] || "",
              detailRun.createdByName ? `gestartet von ${detailRun.createdByName}` : "",
              detailRun.transferredByName
                ? `übernommen von ${detailRun.transferredByName}${
                    detailRun.transferredAt ? ` am ${formatTimestamp(detailRun.transferredAt)}` : ""
                  }`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        <StatusChip status={status} />
      </Box>

      {showPreviewGrid ? null : (
        <PendingState
          status={status}
          progress={progress}
          nowTick={nowTick}
          isSuperAdmin={isSuperAdmin}
          workerAlive={workerAlive}
          isBusy={isBusy}
          cancelRequested={detailRun.cancelRequested}
          errorMessage={detailRun.errorMessage}
          failureHints={detailRun.failureHints}
          feedback={feedback}
          onCancel={onCancel}
        />
      )}

      {showPreviewGrid ? (
        <Box className="spv2-solver-columns">
          <Box className="spv2-solver-side">
            <StatCard value={newCount} label={newCount === 1 ? "neue Schicht" : "neue Schichten"} tone="brand" />
            {coverage ? (
              <StatCard
                value={`${coverage.percent}%`}
                label={coverage.missing ? `des Bedarfs gedeckt · ${coverage.missing} offen` : "des Bedarfs gedeckt"}
                tone={coverage.missing ? "warn" : "ok"}
                barPercent={coverage.percent}
              />
            ) : null}
            <StatCard value={plannedEmployeeCount} label="Mitarbeitende erhalten Schichten" />
            {keptCount ? <StatCard value={keptCount} label="bestehende Schichten bleiben" tone="muted" /> : null}

            {/*
              Übernehmen dauert je nach Planungsgrösse — ohne Rückmeldung sieht
              der Knopf nur grau aus und man weiss nicht, ob etwas passiert
              (Wunsch 31.08.). Deshalb erst „Wird übernommen…" mit Ring,
              danach die Fertig-Meldung mit der Zahl.
            */}
            {transferDone ? (
              <Box className="spv2-solver-transfer-done" role="status">
                <span className="spv2-solver-transfer-done-head">
                  <IconCheck sx={{ fontSize: 18 }} aria-hidden="true" />
                  {transferDone.count === 1
                    ? "1 Schicht übernommen"
                    : `${transferDone.count} Schichten übernommen`}
                </span>
                <span className="spv2-solver-transfer-done-note">
                  Sie stehen jetzt als Entwurf im Schichtplan.
                </span>
              </Box>
            ) : null}

            {status === "done" && !transferDone ? (
              <Box className="spv2-solver-side-actions">
                <AppButton variant="primary" onClick={onRequestTransfer} disabled={isBusy}>
                  {transferPending ? (
                    <>
                      <span className="spv2-solver-btn-spinner" aria-hidden="true" />
                      Wird übernommen…
                    </>
                  ) : (
                    "In den Schichtplan übernehmen"
                  )}
                </AppButton>
                <AppButton variant="outlined" onClick={onRequestDiscard} disabled={isBusy}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  Verwerfen
                </AppButton>
              </Box>
            ) : null}

            {transferOutcome?.conflicts?.length ? (
              <Box className="spv2-solver-error">
                <Typography variant="body2">Nicht übernommen:</Typography>
                {transferOutcome.conflicts.map((conflict) => (
                  <Typography key={conflict.assignment_id} variant="caption" display="block">
                    MA {conflict.user_id} am {formatDateShort(conflict.date) || conflict.date}: {conflict.reason}
                  </Typography>
                ))}
              </Box>
            ) : null}

            {feedback ? (
              <Typography variant="body2" color={feedback.tone === "error" ? "error" : "success.main"}>
                {feedback.text}
              </Typography>
            ) : null}

            <Box className="spv2-solver-legend">
              <span className="spv2-solver-legend-row">
                <span className="spv2-solver-legend-swatch is-new" aria-hidden="true" />
                Neu geplante Schichten
              </span>
              <span className="spv2-solver-legend-row">
                <span className="spv2-solver-legend-swatch is-existing" aria-hidden="true" />
                Bereits vorhanden: Schichten, Absenzen und Ferien
              </span>
              <span className="spv2-solver-legend-note">
                Der Plan lässt sich hier ansehen, aber nicht bearbeiten.
              </span>
            </Box>
          </Box>

          <Box className="spv2-solver-grid">
            <ShiftplanMonthGrid
              viewMode="month"
              rowMode="employee"
              days={days}
              rows={rows}
              shiftsByCell={shiftsByCell}
              userHolidaysByCell={userHolidaysByCell}
              workloadByEmployeeId={workloadByEmployeeId}
              publicHolidayNamesByDate={publicHolidayNamesByDate}
              shiftDisplayMode={shiftDisplayMode || SHIFT_DISPLAY_MODES.TIME_DEPARTMENT}
              releasePreviewShiftIds={newEntryIds}
              dimUnhighlighted
              demandAvailable={demandModel.hasDemand}
              demandVisible={demandVisible}
              demandByDay={demandModel.byDay}
              demandExpanded={demandExpanded}
              demandCompact
              demandTotalMissing={demandModel.totalMissing}
              demandTotalOver={demandModel.totalOver}
              onDemandVisibleChange={setDemandVisible}
              onDemandToggleExpanded={() => setDemandExpanded((current) => !current)}
            />
          </Box>
        </Box>
      ) : null}

      {isSuperAdmin ? <SuperAdminDiagnostics detailRun={detailRun} logRef={logRef} /> : null}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

export default function SolverPanel({
  open = false,
  referenceDate,
  accessLevel = "",
  shiftDisplayMode,
  runs = [],
  availableLocations = [],
  currentLocationId = null,
  workerAlive = null,
  onRefreshRuns,
  onClose,
  onTransferred,
}) {
  const isSuperAdmin = accessLevel === "Super_Admin";
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [directory, setDirectory] = useState([]);
  const lastRunLabel = useMemo(() => {
    const withTimestamp = runs
      .map((run) => run.startedAt || run.createdAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a));
    return withTimestamp.length ? formatTimestamp(withTimestamp[0]) : "";
  }, [runs]);
  const [feedback, setFeedback] = useState(null); // { tone: "ok"|"error", text }
  const [isBusy, setIsBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, run }
  const [transferOutcome, setTransferOutcome] = useState(null);
  // Läuft die Übernahme gerade / ist sie fertig? Steuert Ring und Fertig-Meldung.
  const [transferPending, setTransferPending] = useState(false);
  const [transferDone, setTransferDone] = useState(null); // { count }
  const [handoverPhase, setHandoverPhase] = useState(null); // "lift" | "fly"
  const timers = useRef([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedRunId(null);
    setDetail(null);
    setPreview(null);
    setFeedback(null);
    setConfirmAction(null);
    setTransferOutcome(null);
    setTransferPending(false);
    setTransferDone(null);
    setHandoverPhase(null);
    onRefreshRuns?.();
    getSolverDirectory()
      .then(setDirectory)
      .catch(() => setDirectory([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => clearTimers, [clearTimers]);

  // AppSelect-Menues, Tooltips und der DatePicker-Kalender portalen auf
  // <body> mit einem DS-/MUI-eigenen z-index (1300 / 2610) unterhalb dieses
  // Dialogs (3300) -- ohne Hebel pro Instanz (anders als MUI: kein
  // MenuProps/slotProps-Durchgriff). Die Body-Klasse hebt sie nur an,
  // waehrend dieser Dialog offen ist (solver.css).
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("spv2-solver-dialog-open");
    return () => document.body.classList.remove("spv2-solver-dialog-open");
  }, [open]);

  const selectedRun = useMemo(() => runs.find((run) => run.id === selectedRunId) || null, [runs, selectedRunId]);

  useEffect(() => {
    if (!open || !selectedRunId) {
      setDetail(null);
      return undefined;
    }
    let ignore = false;
    const loadDetail = () => {
      getSolverRun(selectedRunId)
        .then((run) => {
          if (!ignore) setDetail({ ...run, progressReceivedAt: Date.now() });
        })
        .catch(() => {});
    };
    loadDetail();
    const timer = window.setInterval(loadDetail, DETAIL_POLL_MS);
    return () => {
      ignore = true;
      window.clearInterval(timer);
    };
  }, [open, selectedRunId]);

  const previewStatus = selectedRun && PREVIEW_STATUSES.has(selectedRun.status) ? selectedRun.status : null;
  useEffect(() => {
    if (!selectedRunId || !previewStatus) {
      setPreview(null);
      return undefined;
    }
    let ignore = false;
    getSolverRunPreview(selectedRunId)
      .then((data) => {
        if (!ignore) setPreview(data.preview);
      })
      .catch((error) => {
        if (!ignore) setFeedback({ tone: "error", text: `Vorschau konnte nicht geladen werden: ${errorText(error)}` });
      });
    return () => {
      ignore = true;
    };
  }, [selectedRunId, previewStatus]);

  const selectRun = useCallback((runId) => {
    setSelectedRunId(runId);
    setFeedback(null);
    setConfirmAction(null);
    setTransferOutcome(null);
    setTransferDone(null);
  }, []);

  const handleStart = async (config) => {
    setIsBusy(true);
    setFeedback(null);
    try {
      const run = await createSolverRun(config);
      await onRefreshRuns?.();
      selectRun(run.id);
    } catch (error) {
      const data = error?.response?.data;
      setFeedback({ tone: "error", text: errorText(error) });
      // Bereits laufende Planung: direkt dorthin springen, dort kann man sie abbrechen.
      if (data?.code === "run_in_progress" && data?.run_id) {
        await onRefreshRuns?.();
        setSelectedRunId(data.run_id);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const runAction = async (action, runId, successText) => {
    if (!runId) return null;
    setIsBusy(true);
    setFeedback(null);
    try {
      const result = await action(runId);
      if (successText) setFeedback({ tone: "ok", text: successText });
      await onRefreshRuns?.();
      return result;
    } catch (error) {
      setFeedback({ tone: "error", text: errorText(error) });
      return null;
    } finally {
      setIsBusy(false);
    }
  };

  /*
    Übergabe-Choreografie: der Plan dahinter lädt SOFORT neu, damit er die
    neuen Schichten schon zeigt, während der Dialog in zwei Stufen
    (kurz anheben, dann nach oben wegfliegen) verschwindet.
  */
  const playHandover = useCallback(() => {
    onTransferred?.();
    if (prefersReducedMotion()) {
      onClose?.();
      return;
    }
    setHandoverPhase("lift");
    timers.current.push(window.setTimeout(() => setHandoverPhase("fly"), 180));
    timers.current.push(
      window.setTimeout(() => {
        onClose?.();
        setHandoverPhase(null);
      }, HANDOVER_MS)
    );
  }, [onClose, onTransferred]);

  const handleTransferConfirmed = async () => {
    const runId = confirmAction?.run?.id;
    setConfirmAction(null);
    setTransferPending(true);
    let outcome;
    try {
      outcome = await runAction(transferSolverRun, runId);
    } finally {
      setTransferPending(false);
    }
    if (!outcome) return;
    if (outcome.conflicts.length) {
      setTransferOutcome(outcome);
      setFeedback({
        tone: "error",
        text: `${outcome.transferred} Schichten übernommen, ${outcome.conflicts.length} konnten nicht übernommen werden.`,
      });
      onTransferred?.();
      return;
    }
    // Fertig-Meldung stehen lassen, dann erst die Übergabe-Choreografie.
    setTransferDone({ count: outcome.transferred });
    timers.current.push(window.setTimeout(playHandover, TRANSFER_DONE_MS));
  };

  const handleDiscardConfirmed = async () => {
    const runId = confirmAction?.run?.id;
    setConfirmAction(null);
    await runAction(discardSolverRun, runId, "Die Planung wurde verworfen.");
  };

  const handleDeleteConfirmed = async () => {
    const runId = confirmAction?.run?.id;
    setConfirmAction(null);
    const deleted = await runAction(deleteSolverRun, runId, "Die Planung wurde gelöscht.");
    if (deleted && selectedRunId === runId) setSelectedRunId(null);
  };

  const previewNewCount = useMemo(
    () => (preview?.entries || []).filter((entry) => entry.solverSource === "solver").length,
    [preview]
  );
  /*
    Breit wird der Dialog nur, wenn wirklich ein Plan darin steht. Beim
    Rechnen, Warten oder nach einem Fehlschlag bleibt er schlank — sonst
    steht unter der Statusmeldung eine leere Fläche.
  */
  /*
    Die Grösse hängt daran, was die Planung ZEIGEN WIRD — nicht daran, ob die
    Vorschau schon geladen ist. Sonst springt der Dialog beim Öffnen einer
    fertigen Planung erst klein und dann gross.
  */
  const expectsPlan = Boolean(selectedRun && PREVIEW_STATUSES.has(selectedRun.status));
  const dialogClassName = [
    "spv2-solver-dialog",
    expectsPlan ? "is-detail" : "",
    selectedRun && !expectsPlan ? "is-compact" : "",
    handoverPhase ? `is-handover-${handoverPhase}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <ShiftplanDialog
        open={open}
        onClose={handoverPhase ? undefined : onClose}
        title="Automatisch planen"
        icon={<AutoFixHighOutlinedIcon fontSize="small" />}
        size="xl"
        className={dialogClassName}
        bodyClassName="spv2-solver-body"
        headerActions={
          !selectedRun && lastRunLabel ? (
            <AppChip variant="neutral" appearance="outlined" size="sm">
              Letzter Lauf: {lastRunLabel}
            </AppChip>
          ) : null
        }
      >
        {selectedRun ? (
          <DetailView
            run={selectedRun}
            detail={detail}
            preview={preview}
            isSuperAdmin={isSuperAdmin}
            workerAlive={workerAlive}
            isBusy={isBusy}
            transferPending={transferPending}
            transferDone={transferDone}
            feedback={feedback}
            transferOutcome={transferOutcome}
            shiftDisplayMode={shiftDisplayMode}
            onBack={() => selectRun(null)}
            onRequestTransfer={() => setConfirmAction({ type: "transfer", run: selectedRun })}
            onRequestDiscard={() => setConfirmAction({ type: "discard", run: selectedRun })}
            onCancel={() =>
              runAction(cancelSolverRun, selectedRun.id, "Die Berechnung wird abgebrochen.")
            }
          />
        ) : (
          <StartView
            referenceDate={referenceDate}
            directory={directory}
            availableLocations={availableLocations}
            currentLocationId={currentLocationId}
            runs={runs}
            isBusy={isBusy}
            feedback={feedback}
            onStart={handleStart}
            onSelectRun={selectRun}
            onDeleteRun={(run) => setConfirmAction({ type: "delete", run })}
            onClose={onClose}
          />
        )}
      </ShiftplanDialog>

      {/* DS-Nachfragedialoge über dem Solver-Dialog (z-index in solver.css). */}
      {confirmAction?.type === "transfer" ? (
        <AppConfirmDialog
          open
          rootClassName="spv2-solver-confirm-root"
          title="In den Schichtplan übernehmen?"
          icon={<AutoFixHighOutlinedIcon />}
          description={
            confirmAction.run?.existingMode === "overwrite"
              ? `${previewNewCount} Schichten werden als Entwurf in den Schichtplan eingetragen. Die bereits geplanten Schichten im Zeitraum werden dabei ersetzt.`
              : `${previewNewCount} Schichten werden als Entwurf in den Schichtplan eingetragen. Bereits geplante Schichten bleiben unverändert.`
          }
          confirmLabel="Übernehmen"
          onConfirm={handleTransferConfirmed}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}
      {confirmAction?.type === "discard" ? (
        <AppConfirmDialog
          open
          rootClassName="spv2-solver-confirm-root"
          title="Planung verwerfen?"
          icon={<DeleteOutlineIcon />}
          description="Das Ergebnis wird verworfen und lässt sich danach nicht mehr in den Schichtplan übernehmen."
          confirmLabel="Verwerfen"
          confirmTone="error"
          onConfirm={handleDiscardConfirmed}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}
      {confirmAction?.type === "delete" ? (
        <AppConfirmDialog
          open
          rootClassName="spv2-solver-confirm-root"
          title="Planung löschen?"
          icon={<DeleteOutlineIcon />}
          description={
            confirmAction.run?.status === "transferred"
              ? "Die Planung wird aus der Liste entfernt. Die bereits übernommenen Schichten bleiben im Schichtplan bestehen."
              : "Die Planung wird mit ihrem Ergebnis endgültig aus der Liste entfernt."
          }
          confirmLabel="Löschen"
          confirmTone="error"
          onConfirm={handleDeleteConfirmed}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}
    </>
  );
}
